import { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import { biomassColor, riskOf, shiftBelt } from "@/lib/geo";

function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 600, h: 600 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ w: Math.max(200, r.width), h: Math.max(200, r.height) });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

export default function GlobeView({ data, layers, forecastHour, selectedIsland, onSelectIsland }) {
  const globeEl = useRef();
  const [ref, size] = useSize();
  const [ready, setReady] = useState(false);

  const belt = useMemo(() => {
    if (!data?.belt) return [];
    const shifted = shiftBelt(data.belt, forecastHour);
    // only render genuine mats: drop the faint low-density fringe so the belt reads as the
    // real Great Atlantic Sargassum Belt, not a green blanket over the whole basin.
    const maxD = shifted.reduce((m, p) => Math.max(m, p.density || 0), 0.001);
    const cut = maxD * 0.22;
    return shifted.filter((p) => (p.density || 0) >= cut);
  }, [data, forecastHour]);
  const islands = useMemo(() => data?.islands || [], [data]);
  const paths = useMemo(() => {
    if (!data?.predicted_paths) return [];
    return data.predicted_paths.map((p) => ({
      startLat: p.start.lat,
      startLng: p.start.lng,
      endLat: p.end.lat,
      endLng: p.end.lng,
      risk: p.risk,
    }));
  }, [data]);

  const pulseIslands = useMemo(
    () => islands.filter((i) => i.risk === "HIGH" || i.risk === "MODERATE"),
    [islands],
  );

  useEffect(() => {
    if (!globeEl.current) return;
    const controls = globeEl.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.35;
    controls.enableZoom = true;
    globeEl.current.pointOfView({ lat: 14, lng: -62, altitude: 1.9 }, 0);
    setReady(true);
  }, []);

  // focus globe on the selected island
  useEffect(() => {
    if (!globeEl.current || !selectedIsland) return;
    const isl = islands.find((i) => i.id === selectedIsland);
    if (isl) globeEl.current.pointOfView({ lat: isl.lat, lng: isl.lng, altitude: 1.5 }, 1200);
  }, [selectedIsland, islands]);

  return (
    <div ref={ref} className="relative h-full w-full" data-testid="globe-view">
      {size.w > 0 && (
        <Globe
          ref={globeEl}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
          bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
          atmosphereColor="#00e5ff"
          atmosphereAltitude={0.22}
          // ---- biomass belt ----
          hexBinPointsData={layers.biomass ? belt : []}
          hexBinPointLat={(d) => d.lat}
          hexBinPointLng={(d) => d.lng}
          hexBinPointWeight={(d) => d.density}
          hexBinResolution={4}
          hexBinMerge={false}
          hexAltitude={(d) => Math.min(0.25, d.sumWeight * 0.018)}
          hexTopColor={(d) => biomassColor(Math.min(1, d.sumWeight / 5))}
          hexSideColor={() => "rgba(0,255,135,0.25)"}
          hexTransitionDuration={600}
          // ---- predicted drift paths ----
          arcsData={layers.drift ? paths : []}
          arcStartLat={(d) => d.startLat}
          arcStartLng={(d) => d.startLng}
          arcEndLat={(d) => d.endLat}
          arcEndLng={(d) => d.endLng}
          arcColor={(d) => {
            const c = riskOf(d.risk).hex;
            return [c, "#00ff87"];
          }}
          arcStroke={0.7}
          arcAltitudeAutoScale={0.45}
          arcDashLength={0.35}
          arcDashGap={2}
          arcDashInitialGap={() => Math.random() * 2}
          arcDashAnimateTime={2600}
          // ---- island markers ----
          pointsData={layers.islands ? islands : []}
          pointLat={(d) => d.lat}
          pointLng={(d) => d.lng}
          pointColor={(d) => riskOf(d.risk).hex}
          pointAltitude={0.012}
          pointRadius={(d) => (d.id === selectedIsland ? 0.5 : 0.3)}
          onPointClick={(d) => onSelectIsland?.(d.id)}
          pointLabel={(d) =>
            `<div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#e2e8f0;background:rgba(3,11,30,0.9);padding:4px 8px;border:1px solid rgba(0,229,255,0.3);border-radius:6px">${d.name} — ${d.risk}${d.hours_to_landing != null ? " · " + d.hours_to_landing + "h" : ""}</div>`
          }
          labelsData={layers.islands ? islands : []}
          labelLat={(d) => d.lat}
          labelLng={(d) => d.lng}
          labelText={(d) => d.name}
          labelSize={0.75}
          labelDotRadius={0.001}
          labelColor={() => "rgba(226,232,240,0.85)"}
          labelResolution={1}
          labelAltitude={0.02}
          // ---- pulsing alert rings ----
          ringsData={layers.islands ? pulseIslands : []}
          ringLat={(d) => d.lat}
          ringLng={(d) => d.lng}
          ringMaxRadius={3.5}
          ringPropagationSpeed={2.4}
          ringRepeatPeriod={1000}
          ringColor={(d) => {
            const c = d.risk === "HIGH" ? "255,159,28" : "255,209,102";
            return (t) => `rgba(${c},${Math.max(0, 1 - t)})`;
          }}
        />
      )}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="eyebrow text-cyan-400/70">Initialising orbital telemetry…</span>
        </div>
      )}
    </div>
  );
}
